import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const BulkCreationModal = ({ onClose, onCreateBulk }) => {
  const [bulkData, setBulkData] = useState({
    templateId: '',
    startDate: '',
    endDate: '',
    selectedDays: [],
    departments: [],
    adjustments: {
      modifyTimes: false,
      newStartTime: '',
      newEndTime: '',
      modifyStaffing: false,
      newMaxEmployees: 1
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Mock data
  const templates = [
    { value: 'morning-cs', label: 'Morgenschicht Kundenservice' },
    { value: 'night-security', label: 'Nachtschicht Sicherheit' },
    { value: 'production', label: 'Produktionsschicht' },
    { value: 'weekend', label: 'Wochenendschicht' },
    { value: 'admin-part', label: 'Verwaltung Teilzeit' }
  ];

  const departments = [
    { value: 'customer-service', label: 'Kundenservice' },
    { value: 'operations', label: 'Betrieb' },
    { value: 'manufacturing', label: 'Fertigung' },
    { value: 'administration', label: 'Verwaltung' },
    { value: 'security', label: 'Sicherheit' }
  ];

  const daysOfWeek = [
    { value: 'monday', label: 'Montag' },
    { value: 'tuesday', label: 'Dienstag' },
    { value: 'wednesday', label: 'Mittwoch' },
    { value: 'thursday', label: 'Donnerstag' },
    { value: 'friday', label: 'Freitag' },
    { value: 'saturday', label: 'Samstag' },
    { value: 'sunday', label: 'Sonntag' }
  ];

  const handleInputChange = (field, value) => {
    setBulkData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleAdjustmentChange = (field, value) => {
    setBulkData(prev => ({
      ...prev,
      adjustments: {
        ...prev?.adjustments,
        [field]: value
      }
    }));
  };

  const handleDayToggle = (day) => {
    setBulkData(prev => ({
      ...prev,
      selectedDays: prev?.selectedDays?.includes(day)
        ? prev?.selectedDays?.filter(d => d !== day)
        : [...prev?.selectedDays, day]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!bulkData?.templateId) {
      newErrors.templateId = 'Vorlage ist erforderlich';
    }

    if (!bulkData?.startDate) {
      newErrors.startDate = 'Startdatum ist erforderlich';
    }

    if (!bulkData?.endDate) {
      newErrors.endDate = 'Enddatum ist erforderlich';
    }

    if (bulkData?.startDate && bulkData?.endDate && bulkData?.startDate >= bulkData?.endDate) {
      newErrors.endDate = 'Enddatum muss nach dem Startdatum liegen';
    }

    if (bulkData?.selectedDays?.length === 0) {
      newErrors.selectedDays = 'Mindestens ein Tag muss ausgewählt werden';
    }

    if (bulkData?.departments?.length === 0) {
      newErrors.departments = 'Mindestens eine Abteilung muss ausgewählt werden';
    }

    if (bulkData?.adjustments?.modifyTimes) {
      if (!bulkData?.adjustments?.newStartTime) {
        newErrors.newStartTime = 'Neue Startzeit ist erforderlich';
      }
      if (!bulkData?.adjustments?.newEndTime) {
        newErrors.newEndTime = 'Neue Endzeit ist erforderlich';
      }
      if (bulkData?.adjustments?.newStartTime && bulkData?.adjustments?.newEndTime && 
          bulkData?.adjustments?.newStartTime >= bulkData?.adjustments?.newEndTime) {
        newErrors.newEndTime = 'Endzeit muss nach der Startzeit liegen';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const calculateShiftCount = () => {
    if (!bulkData?.startDate || !bulkData?.endDate || bulkData?.selectedDays?.length === 0) {
      return 0;
    }

    const start = new Date(bulkData.startDate);
    const end = new Date(bulkData.endDate);
    const dayCount = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const weeksCount = Math.ceil(dayCount / 7);
    
    return weeksCount * bulkData?.selectedDays?.length * bulkData?.departments?.length;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (onCreateBulk) {
        onCreateBulk(bulkData);
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating bulk shifts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Schichten in großen Mengen erstellen</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Erstellen Sie mehrere Schichten basierend auf einer Vorlage
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Template Selection */}
            <Select
              label="Vorlage auswählen"
              placeholder="Vorlage für Schichterstellung auswählen"
              options={templates}
              value={bulkData?.templateId}
              onChange={(value) => handleInputChange('templateId', value)}
              error={errors?.templateId}
              required
            />

            {/* Date Range */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Input
                label="Startdatum"
                type="date"
                value={bulkData?.startDate}
                onChange={(e) => handleInputChange('startDate', e?.target?.value)}
                error={errors?.startDate}
                required
              />

              <Input
                label="Enddatum"
                type="date"
                value={bulkData?.endDate}
                onChange={(e) => handleInputChange('endDate', e?.target?.value)}
                error={errors?.endDate}
                required
              />
            </div>

            {/* Days Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Wochentage auswählen *
              </label>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {daysOfWeek?.map((day) => (
                  <Checkbox
                    key={day?.value}
                    label={day?.label}
                    checked={bulkData?.selectedDays?.includes(day?.value)}
                    onChange={() => handleDayToggle(day?.value)}
                  />
                ))}
              </div>
              {errors?.selectedDays && (
                <p className="text-sm text-error mt-1">{errors?.selectedDays}</p>
              )}
            </div>

            {/* Departments */}
            <Select
              label="Abteilungen"
              placeholder="Abteilungen für Schichterstellung auswählen"
              options={departments}
              value={bulkData?.departments}
              onChange={(value) => handleInputChange('departments', value)}
              error={errors?.departments}
              multiple
              required
            />

            {/* Adjustments */}
            <div className="border-t border-border pt-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Anpassungen</h3>
              
              <div className="space-y-4">
                <Checkbox
                  label="Zeiten anpassen"
                  description="Überschreiben Sie die Zeiten aus der Vorlage"
                  checked={bulkData?.adjustments?.modifyTimes}
                  onChange={(e) => handleAdjustmentChange('modifyTimes', e?.target?.checked)}
                />

                {bulkData?.adjustments?.modifyTimes && (
                  <div className="ml-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Input
                      label="Neue Startzeit"
                      type="time"
                      value={bulkData?.adjustments?.newStartTime}
                      onChange={(e) => handleAdjustmentChange('newStartTime', e?.target?.value)}
                      error={errors?.newStartTime}
                      required
                    />

                    <Input
                      label="Neue Endzeit"
                      type="time"
                      value={bulkData?.adjustments?.newEndTime}
                      onChange={(e) => handleAdjustmentChange('newEndTime', e?.target?.value)}
                      error={errors?.newEndTime}
                      required
                    />
                  </div>
                )}

                <Checkbox
                  label="Personalbesetzung anpassen"
                  description="Überschreiben Sie die Mitarbeiteranzahl aus der Vorlage"
                  checked={bulkData?.adjustments?.modifyStaffing}
                  onChange={(e) => handleAdjustmentChange('modifyStaffing', e?.target?.checked)}
                />

                {bulkData?.adjustments?.modifyStaffing && (
                  <div className="ml-6">
                    <Input
                      label="Neue Mitarbeiteranzahl"
                      type="number"
                      min="1"
                      max="50"
                      value={bulkData?.adjustments?.newMaxEmployees}
                      onChange={(e) => handleAdjustmentChange('newMaxEmployees', parseInt(e?.target?.value) || 1)}
                      required
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            {calculateShiftCount() > 0 && (
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Info" size={16} className="text-accent" />
                  <span className="font-medium text-foreground">Zusammenfassung</span>
                </div>
                <p className="text-sm text-foreground">
                  Es werden <strong>{calculateShiftCount()} Schichten</strong> erstellt basierend auf Ihrer Auswahl.
                </p>
                <div className="mt-2 text-xs text-muted-foreground">
                  <p>• Zeitraum: {bulkData?.startDate} bis {bulkData?.endDate}</p>
                  <p>• Tage: {bulkData?.selectedDays?.length} pro Woche</p>
                  <p>• Abteilungen: {bulkData?.departments?.length}</p>
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-4 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Abbrechen
          </Button>
          
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
            disabled={calculateShiftCount() === 0}
          >
            {calculateShiftCount()} Schichten erstellen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkCreationModal;